import { Euler, Quaternion, Vector3 } from 'three'
import { Pilot } from '../physics/Pilot'
import { makeConfig, type TuningConfig } from '../physics/config'
import { ScriptSource, type ScriptFn } from '../input/ScriptSource'
import { gripById } from '../input/gripPresets'
import { makePilotInput } from '../input/types'

/** Fixed 90 Hz to match the display, exactly as the headset runs it. */
export const SIM_DT = 1 / 90

export interface TraceRow {
  t: number
  /** Altitude of the CoM above its standing height, m. */
  altitude: number
  vy: number
  speed: number
  /** Angle between the pilot's up axis and world up, degrees. */
  tilt: number
  /** Signed roll about the body's forward axis, degrees. */
  roll: number
  /** Signed pitch, degrees. */
  pitch: number
  /** Yaw angle, degrees (unwrapped). */
  yaw: number
  /** Roll rate about world axes, deg/s. */
  rollRate: number
  yawRate: number
  x: number
  y: number
  z: number
  thrust: number
  /** Magnitude of the applied torque, N·m. */
  torque: number
}

export interface RunOptions {
  duration: number
  config?: Partial<TuningConfig>
  grip?: string
  /** Start airborne at hover instead of standing on the ground. */
  startAirborne?: boolean
  startAltitude?: number
  /** Initial tilt perturbation, degrees about +Z (roll). */
  initialTilt?: number
  /** Sample every Nth step into the trace (physics still runs at SIM_DT). */
  sampleEvery?: number
  /** Called after every physics step — for driving game logic inside a run. */
  onStep?: (pilot: Pilot, t: number) => void
}

export interface RunResult {
  trace: TraceRow[]
  pilot: Pilot
  config: TuningConfig
  hoverThrottle: number
}

/**
 * Run a scripted flight through the real `Pilot` at a fixed timestep and record
 * a trace. No renderer, no browser, no input device — this is layer 1 of the
 * desktop sim (DESIGN.md §4).
 */
export function runScript(script: ScriptFn, opts: RunOptions): RunResult {
  const config = makeConfig('realistic')
  Object.assign(config, opts.config ?? {})

  const pilot = new Pilot(config)
  const input = makePilotInput()
  const source = new ScriptSource(script, gripById(opts.grip ?? 'high'), config.nominalHeight)

  // Calibrate from a neutral standing pose before anything moves.
  source.sample(input, 0)
  pilot.com.calibrate(input.head, config)
  pilot.syncBodyProperties()

  const startY = opts.startAirborne
    ? pilot.com.height + (opts.startAltitude ?? 20)
    : pilot.com.height
  const startQ = new Quaternion()
  if (opts.initialTilt) {
    startQ.setFromAxisAngle(new Vector3(0, 0, 1), (opts.initialTilt * Math.PI) / 180)
  }
  pilot.reset(new Vector3(0, startY, 0), startQ)

  const steps = Math.round(opts.duration / SIM_DT)
  const sampleEvery = opts.sampleEvery ?? 1
  const trace: TraceRow[] = []
  const euler = { roll: 0, pitch: 0, yaw: 0 }

  for (let i = 0; i <= steps; i++) {
    const t = i * SIM_DT
    if (i % sampleEvery === 0) {
      readEuler(pilot.body.orientation, euler)
      trace.push({
        t,
        altitude: pilot.body.position.y - startY,
        vy: pilot.body.velocity.y,
        speed: pilot.body.velocity.length(),
        tilt: pilot.tiltDegrees(),
        roll: euler.roll,
        pitch: euler.pitch,
        yaw: euler.yaw,
        rollRate: pilot.body.omega.z * (180 / Math.PI),
        yawRate: pilot.body.omega.y * (180 / Math.PI),
        x: pilot.body.position.x,
        y: pilot.body.position.y,
        z: pilot.body.position.z,
        thrust: pilot.debug.thrustTotal,
        torque: pilot.debug.netTorque.length(),
      })
    }
    if (i === steps) break
    source.state.bodyQuaternion.copy(pilot.body.orientation)
    source.state.tiltDegrees = pilot.tiltDegrees()
    source.state.altitude = pilot.altitude
    source.state.position.copy(pilot.body.position)
    source.state.velocity.copy(pilot.body.velocity)
    source.state.grounded = pilot.grounded
    source.sample(input, SIM_DT)
    pilot.step(input, SIM_DT)
    opts.onStep?.(pilot, t + SIM_DT)
  }

  return { trace, pilot, config, hoverThrottle: pilot.hoverThrottle }
}

/** ZYX-ish decomposition that stays readable for a mostly-upright pilot. */
function readEuler(q: Quaternion, out: { roll: number; pitch: number; yaw: number }): void {
  _e.setFromQuaternion(q, 'YXZ')
  out.yaw = (_e.y * 180) / Math.PI
  out.pitch = (_e.x * 180) / Math.PI
  out.roll = (_e.z * 180) / Math.PI
}

const _e = new Euler()

// ---------------------------------------------------------------------------
// trace metrics
// ---------------------------------------------------------------------------

export function finalRow(trace: TraceRow[]): TraceRow {
  return trace[trace.length - 1]
}

export function maxAbs(trace: TraceRow[], key: keyof TraceRow): number {
  let m = 0
  for (const row of trace) m = Math.max(m, Math.abs(row[key] as number))
  return m
}

export function at(trace: TraceRow[], time: number): TraceRow {
  let best = trace[0]
  for (const row of trace) {
    if (row.t <= time) best = row
    else break
  }
  return best
}

/** First time `key` exceeds `threshold`, or Infinity if it never does. */
export function timeToExceed(trace: TraceRow[], key: keyof TraceRow, threshold: number): number {
  for (const row of trace) {
    if (Math.abs(row[key] as number) >= threshold) return row.t
  }
  return Infinity
}
